// Load Workspace Modal Component

import React, { useState, useMemo, useEffect } from 'react';
import { ModalCard } from '@/components/ui';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { useLoadWorkspaceToAnalysis } from '@/hooks/useLoadWorkspaceToAnalysis';
import { createWorkspaceContextFromLoaded } from '@/hooks/useWorkspaceContext';
import { LoadWorkspaceOptions, Workspace } from '@/types/workspace';
import { WorkspaceContext } from '@/types/workspaceContext';
import { generateSampleWorkspaces } from '@/services/api/workspaces';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentIcon,
  ArrowPathIcon,
  UserIcon,
  CheckBadgeIcon,
  ArrowDownCircleIcon,
} from '@heroicons/react/24/outline';

interface LoadWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoadWorkspace: (workspace: Workspace, workspaceContext: WorkspaceContext) => void;

  // Workspace type for filtering workspaces
  workspaceType: 'weibull' | 'vbl';
}

const LoadWorkspaceModal: React.FC<LoadWorkspaceModalProps> = ({
  isOpen,
  onClose,
  onLoadWorkspace,
  workspaceType,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'name' | 'updatedAt' | 'createdAt'>('updatedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<{
    id: string;
    name: string;
    sampleSize: number;
  } | null>(null);
  const [loadType, setLoadType] = useState<'snapshot' | 'recalculate'>('snapshot');

  // Generate sample workspaces on first load for development/demo
  useEffect(() => {
    if (isOpen) {
      try {
        generateSampleWorkspaces();
      } catch (error) {
        console.warn('Could not generate sample workspaces:', error);
      }
    }
  }, [isOpen]);


  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedWorkspace(null);
      setLoadType('snapshot');
      setSearchTerm('');
      setSelectedTags([]);
    }
  }, [isOpen]);

  // Load workspace mutation with correct workspace type
  const loadWorkspaceMutation = useLoadWorkspaceToAnalysis(workspaceType);

  // Fetch workspaces with current filters
  const {
    data: workspacesData,
    isLoading,
    error,
  } = useWorkspaces({
    search: searchTerm,
    tags: selectedTags,
    sortBy,
    sortOrder,
    size: 50, // Show more workspaces in modal
    workspaceType, // Filter by workspace type
  });

  // Get all unique tags for filtering
  const allTags = useMemo(() => {
    if (!workspacesData?.workspaces) return [];
    const tagSet = new Set<string>();
    workspacesData.workspaces.forEach((workspace) => {
      workspace.tags?.forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [workspacesData]);

  // Filter and sort workspacesData
  const filteredWorkspaces = useMemo(() => {
    if (!workspacesData?.workspaces) return [];
    let filtered = [...workspacesData.workspaces];

    // Apply search filter
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(workspace =>
        workspace.name.toLowerCase().includes(query) ||
        workspace.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Apply tag filter
    if (selectedTags.length > 0) {
      filtered = filtered.filter(workspace =>
        selectedTags.every(tag => workspace.tags?.includes(tag))
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'updatedAt':
          comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [workspacesData, searchTerm, selectedTags, sortBy, sortOrder]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleSelectWorkspace = (workspace: { id: string; name: string; sampleSize: number }) => {
    setSelectedWorkspace(workspace);
  };

  const handleConfirmLoad = async () => {
    if (!selectedWorkspace) return;

    try {
      const options: LoadWorkspaceOptions = {
        strategy: loadType === 'snapshot' ? 'exact_recreation' : 'dynamic_analysis',
        preserveData: loadType === 'snapshot',
        preserveResults: loadType === 'snapshot',
        preserveFilters: true,
      };

      const response = await loadWorkspaceMutation.mutateAsync({
        id: selectedWorkspace.id,
        options,
      });

      // Create workspace context from loaded workspace
      const workspaceContext = createWorkspaceContextFromLoaded(response.workspace);

      // Pass both workspace and context to parent
      onLoadWorkspace(response.workspace, workspaceContext);

      onClose();
      setSelectedWorkspace(null);
    } catch (error) {
      console.error('Failed to load workspace:', error);
      // Error handling could be improved here
    }
  };

  const handleCancelSelection = () => {
    setSelectedWorkspace(null);
    setLoadType('snapshot');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedTags([]);
    setSortBy('updatedAt');
    setSortOrder('desc');
  };

  // Show load options when workspace is selected
  if (selectedWorkspace) {
    return (
      <ModalCard
        isOpen={isOpen}
        onClose={handleCancelSelection}
        title="Load Workspace Options"
        subtitle={`Choose how to load "${selectedWorkspace.name}"`}
        size="md"
        closeOnBackdrop={false}
      >
        <div className="p-6 space-y-6">
          {/* Workspace Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">{selectedWorkspace.name}</h3>
            <p className="text-sm text-gray-600">
              Contains {selectedWorkspace.sampleSize} failure records
            </p>
          </div>

          {/* Load Type Selection */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Load Options</h4>

            {/* Option 1: Load Snapshot */}
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="loadType"
                value="snapshot"
                checked={loadType === 'snapshot'}
                onChange={(e) => setLoadType(e.target.value as 'snapshot' | 'recalculate')}
                className="mt-1 text-onyx-bright-blue focus:ring-onyx-bright-blue"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <DocumentIcon className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-gray-900">Load snapshot</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Load the exact historical data and results as they were saved. Perfect for
                  regulatory compliance and reproducing past analyses.
                </p>
              </div>
            </label>

            {/* Option 2: Update with New Data */}
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="loadType"
                value="recalculate"
                checked={loadType === 'recalculate'}
                onChange={(e) => setLoadType(e.target.value as 'snapshot' | 'recalculate')}
                className="mt-1 text-onyx-bright-blue focus:ring-onyx-bright-blue"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <ArrowPathIcon className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-gray-900">Update with new data</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Use the saved filters to query current database and recalculate results.
                  Incorporates any new failure records since this workspace was saved.
                </p>
              </div>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              onClick={handleCancelSelection}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleConfirmLoad}
              disabled={loadWorkspaceMutation.isPending}
              data-testid="confirm-load-button"
              className="px-4 py-2 bg-onyx-bright-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadWorkspaceMutation.isPending ? 'Loading...' : 'Load Workspace'}
            </button>
          </div>
        </div>
      </ModalCard>
    );
  }

  return (
    <ModalCard
      isOpen={isOpen}
      onClose={onClose}
      title="Load Workspace"
      subtitle={`Browse and load your saved analysis workspaces${workspacesData ? ` (${workspacesData.total} total)` : ''}`}
      size="xl"
      closeOnBackdrop={true}
    >
      <div className="p-6 space-y-4">
        {/* Search and Filter Bar */}
        <div className="flex items-center space-x-4">
          {/* Search Input */}
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search workspaces by name or description..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-onyx-bright-blue focus:border-onyx-bright-blue"
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${showFilters || selectedTags.length > 0
              ? 'bg-blue-50 border-blue-300 text-blue-700'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
          >
            <FunnelIcon className="w-4 h-4" />
            <span>Filters</span>
            {selectedTags.length > 0 && (
              <span className="bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {selectedTags.length}
              </span>
            )}
          </button>

          {/* Sort Options */}
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortBy(field as 'name' | 'updatedAt' | 'createdAt');
              setSortOrder(order as 'asc' | 'desc');
            }}
            className="pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-onyx-bright-blue focus:border-onyx-bright-blue"
          >
            <option value="updatedAt-desc">Recently Modified</option>
            <option value="createdAt-desc">Recently Created</option>
            <option value="name-asc">Name (A-Z)</option>
            <option value="name-desc">Name (Z-A)</option>
          </select>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-700">Filter by Tags</h4>
              {selectedTags.length > 0 && (
                <button
                  onClick={clearFilters}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear all
                </button>
              )}
            </div>

            {allTags.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => handleTagToggle(tag)}
                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${selectedTags.includes(tag)
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No tags found in your workspaces</p>
            )}
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-onyx-bright-blue"></div>
            <span className="ml-2 text-gray-600">Loading workspaces...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <div className="text-red-600 mb-2">Failed to load workspaces</div>
            <button
              onClick={() => window.location.reload()}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Workspace List */}
        {filteredWorkspaces && (
          <>
            {filteredWorkspaces.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredWorkspaces.map((workspace) => {
                  const sampleSize =
                    workspace.metadata?.totalFailures ||
                    workspace.metadata?.snapshot?.totalRecords ||
                    0;
                  return (
                    <div
                      key={workspace.id}
                      data-testid={`workspace-card-${workspace.id}`}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 transition-colors cursor-pointer"
                      onClick={() =>
                        handleSelectWorkspace({
                          id: workspace.id,
                          name: workspace.name,
                          sampleSize,
                        })
                      }
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="font-medium text-gray-900">{workspace.name}</h3>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              v{workspace.version}
                            </span>
                            {/* Data freshness indicator using icon */}
                            <div className="group relative">
                              {workspace.isLatestData ? (
                                <CheckBadgeIcon className="w-4 h-4 text-green-600" />
                              ) : (
                                <ArrowDownCircleIcon className="w-4 h-4 text-amber-600" />
                              )}
                              <div className="absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                {workspace.isLatestData
                                  ? 'Uses latest dataset'
                                  : 'Uses historical data'}
                              </div>
                            </div>
                          </div>
                          {workspace.description && (
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{workspace.description}</p>
                          )}
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                            <div className="flex items-center space-x-1">
                              <span>Created: {new Date(workspace.createdAt).toLocaleDateString()}</span>
                            </div>
                            <span>•</span>
                            <span>{sampleSize} failure records</span>
                            {workspace.createdBy && (
                              <>
                                <span>•</span>
                                <div className="flex items-center space-x-1">
                                  <UserIcon className="w-3 h-3" />
                                  <span>by {workspace.createdBy.name}</span>
                                </div>
                              </>
                            )}
                            {workspace.tags && workspace.tags.length > 0 && (
                              <>
                                <span>•</span>
                                <div className="flex space-x-1">
                                  {workspace.tags.slice(0, 2).map((tag) => (
                                    <span
                                      key={tag}
                                      className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                  {workspace.tags.length > 2 && (
                                    <span className="text-gray-400">
                                      +{workspace.tags.length - 2}
                                    </span>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <button className="px-3 py-1.5 bg-onyx-bright-blue text-white text-sm rounded hover:bg-blue-700 transition-colors">
                            Load
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-500 mb-2">
                  {searchTerm || selectedTags.length > 0
                    ? 'No workspaces match your filters'
                    : 'No saved workspaces found'}
                </div>
                {(searchTerm || selectedTags.length > 0) && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </ModalCard>
  );
};

export default LoadWorkspaceModal;
